import { MiraCustomerData } from '../../../../../shared/types/customer';
import {
  ComparisonRow,
  ComparisonStatus,
  Customer,
  TransportCustomerPayload,
} from './customerImportTypes';

export function formatDiscount(value?: number | null) {
  return value === null || value === undefined ? '-' : `${value}%`;
}

const MISSING_FIELD_LABELS = {
  name: 'nimi',
  businessId: 'Y-tunnus',
  customerId: 'asiakasnumero',
};

const MISSING_FIELDS_PREFIX = 'Puuttuvat kentät: ';

function indexSqlCustomers(customers: Customer[]) {
  const sqlByCustomerId = new Map<string, Customer>();
  const unmatchedSql: ComparisonRow[] = [];

  customers.forEach((customer) => {
    if (customer.customerId) {
      sqlByCustomerId.set(customer.customerId, customer);
      return;
    }

    unmatchedSql.push({
      key: `sql-${customer.id}`,
      status: 'remove',
      selected: true,
      sql: customer,
      mira: null,
      diffs: ['customerId'],
      hasMissingData: false,
      missingFields: [],
      errorLabel: null,
    });
  });

  return { sqlByCustomerId, unmatchedSql };
}

function indexMiraCustomers(
  miraCustomersWithDiscounts: MiraCustomerData[],
) {
  const miraByCustomerId = new Map<string, MiraCustomerData>();
  const unmatchedMira: ComparisonRow[] = [];

  miraCustomersWithDiscounts.forEach((customer, index) => {
    if (customer.clientId) {
      miraByCustomerId.set(customer.clientId, customer);
      return;
    }

    unmatchedMira.push({
      key: `mira-${customer.clientName ?? 'unknown'}-${index}`,
      status: 'add',
      selected: false,
      sql: null,
      mira: customer,
      diffs: ['customerId'],
      hasMissingData: true,
      missingFields: [MISSING_FIELD_LABELS.customerId],
      errorLabel: `${MISSING_FIELDS_PREFIX}${MISSING_FIELD_LABELS.customerId}`,
      invalidReason: 'Puuttuva asiakasnumero',
    });
  });

  return { miraByCustomerId, unmatchedMira };
}

function getCustomerDiffs(
  sqlCustomer: Customer | null,
  miraCustomer: MiraCustomerData | null,
) {
  const diffs: string[] = [];
  if (!sqlCustomer || !miraCustomer) {
    return diffs;
  }

  if ((miraCustomer.clientName ?? '') !== (sqlCustomer.name ?? '')) {
    diffs.push('name');
  }

  if (
    (miraCustomer.businessId ?? '') !== (sqlCustomer.businessId ?? '')
  ) {
    diffs.push('businessId');
  }

  if (
    (miraCustomer.transportationDiscount ?? 0) !==
    (sqlCustomer.discount ?? 0)
  ) {
    diffs.push('discount');
  }

  return diffs;
}

function getStatus(
  sqlCustomer: Customer | null,
  miraCustomer: MiraCustomerData | null,
  diffs: string[],
): ComparisonStatus {
  if (sqlCustomer) {
    if (miraCustomer) {
      return diffs.length > 0 ? 'modify' : 'unchanged';
    }

    return 'remove';
  }

  return 'add';
}

function getMissingFieldDetails(
  miraCustomer: MiraCustomerData | null,
  status: ComparisonStatus,
) {
  const missingFields: string[] = [];

  if (status === 'add' || status === 'modify') {
    if (!miraCustomer?.clientName) {
      missingFields.push(MISSING_FIELD_LABELS.name);
    }
    if (!miraCustomer?.businessId) {
      missingFields.push(MISSING_FIELD_LABELS.businessId);
    }
    if (!miraCustomer?.clientId) {
      missingFields.push(MISSING_FIELD_LABELS.customerId);
    }
  }

  const hasMissingData = missingFields.length > 0;
  const errorLabel = hasMissingData
    ? `${MISSING_FIELDS_PREFIX}${missingFields.join(', ')}`
    : null;
  const invalidReason =
    status === 'add' && hasMissingData
      ? errorLabel ?? undefined
      : undefined;

  return { missingFields, hasMissingData, errorLabel, invalidReason };
}

export function buildComparisonRows(
  customers: Customer[],
  miraCustomersWithDiscounts: MiraCustomerData[],
): ComparisonRow[] {
  const { sqlByCustomerId, unmatchedSql } =
    indexSqlCustomers(customers);
  const { miraByCustomerId, unmatchedMira } = indexMiraCustomers(
    miraCustomersWithDiscounts,
  );

  const allKeys = new Set<string>([
    ...sqlByCustomerId.keys(),
    ...miraByCustomerId.keys(),
  ]);

  const rows: ComparisonRow[] = [];

  allKeys.forEach((key) => {
    const sqlCustomer = sqlByCustomerId.get(key) ?? null;
    const miraCustomer = miraByCustomerId.get(key) ?? null;

    const diffs = getCustomerDiffs(sqlCustomer, miraCustomer);
    const status = getStatus(sqlCustomer, miraCustomer, diffs);
    const {
      missingFields,
      hasMissingData,
      errorLabel,
      invalidReason,
    } = getMissingFieldDetails(miraCustomer, status);

    rows.push({
      key,
      status,
      selected:
        (status === 'add' ||
          status === 'modify' ||
          status === 'remove') &&
        !hasMissingData &&
        !invalidReason,
      sql: sqlCustomer,
      mira: miraCustomer,
      diffs,
      hasMissingData,
      missingFields,
      errorLabel,
      invalidReason,
    });
  });

  return [...rows, ...unmatchedSql, ...unmatchedMira].sort((a, b) =>
    a.key.localeCompare(b.key),
  );
}

export function buildInitialSelection(rows: ComparisonRow[]) {
  const initial: Record<string, boolean> = {};

  rows.forEach((row) => {
    initial[row.key] =
      row.status !== 'unchanged' &&
      row.selected &&
      !row.hasMissingData &&
      !row.invalidReason;
  });

  return initial;
}

export function buildSyncPayloads(
  rows: ComparisonRow[],
  selectionMap: Record<string, boolean>,
): { upserts: TransportCustomerPayload[]; deleteIds: number[] } {
  const selectedRows = rows.filter((row) => selectionMap[row.key]);

  const upserts: TransportCustomerPayload[] = [];
  const deleteIds: number[] = [];

  selectedRows.forEach((row) => {
    if (row.hasMissingData) {
      return;
    }

    if (row.status === 'remove' && row.sql?.id) {
      deleteIds.push(row.sql.id);
      return;
    }

    if (!row.mira) {
      return;
    }

    const payload: TransportCustomerPayload = {
      name: row.mira.clientName ?? '',
      businessId: row.mira.businessId ?? '',
      customerId: row.mira.clientId ?? undefined,
      discount: row.mira.transportationDiscount ?? 0,
    };

    if (row.sql?.id) {
      payload.id = row.sql.id;
    }

    if (row.status === 'add' || row.status === 'modify') {
      upserts.push(payload);
    }
  });

  return { upserts, deleteIds };
}
